/**
 * Formatação para leitura.
 *
 * A API SEMPRE devolve dinheiro em CENTAVOS INTEIROS — nunca em reais fracionados.
 * A conversão para exibição acontece aqui, num lugar só. Se cada tela dividisse por
 * 100 por conta própria, uma delas acabaria dividindo duas vezes.
 */

export function moeda(centavos: number): string {
  return (centavos / 100)
    .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    // O `Intl` do português usa espaço não separável depois do "R$"; ele é invisível
    // e atrapalha busca e comparação de texto.
    .replace(/ /g, ' ');
}

/** Forma compacta para cartões de indicador: `R$ 1,9 mi`, `R$ 68,6 mil`. */
export function moedaCompacta(centavos: number): string {
  const reais = centavos / 100;
  if (Math.abs(reais) >= 1_000_000) {
    return `R$ ${(reais / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  }
  if (Math.abs(reais) >= 10_000) {
    return `R$ ${(reais / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  }
  return moeda(centavos);
}

/** `2026-03-15` → `15/03/2026`. Trabalha com a string, sem passar por `Date`. */
export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  const partes = iso.slice(0, 10).split('-');
  if (partes.length !== 3) return iso;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

/** Carimbo com hora, para registros de auditoria. */
export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function documento(digitos: string | null | undefined): string {
  const d = (digitos ?? '').replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  return d || '—';
}

/**
 * Número do processo no padrão do CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO.
 *
 * Guardado só com dígitos, como o CPF. Com 20 dígitos sai na máscara; qualquer
 * outro tamanho é protocolo antigo ou administrativo e sai como está — recusar
 * o que não é CNJ só faria a usuária guardar o número em observações de novo.
 */
export function numeroDeProcesso(guardado: string | null | undefined): string {
  const texto = (guardado ?? '').trim();
  const d = texto.replace(/\D/g, '');
  // Fora do padrão do CNJ sai COMO FOI GUARDADO: descartar letras aqui mostraria
  // "201988" para quem cadastrou "PROC-2019/88".
  if (d.length !== 20) return texto || '—';
  return (
    `${d.slice(0, 7)}-${d.slice(7, 9)}.${d.slice(9, 13)}.` +
    `${d.slice(13, 14)}.${d.slice(14, 16)}.${d.slice(16)}`
  );
}

export function telefone(internacional: string | null | undefined): string {
  const d = (internacional ?? '').replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || '—';
}

export function percentual(valor: number): string {
  return `${String(valor).replace('.', ',')}%`;
}

/** `2026-03` → `Março de 2026` */
export function mesPorExtenso(aaaaMM: string): string {
  const nomes = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  const [ano, mes] = aaaaMM.split('-').map(Number);
  return `${nomes[(mes ?? 1) - 1]} de ${ano}`;
}

/** `2026-03` → `mar/26`, para eixo de gráfico onde não cabe o nome inteiro. */
export function mesCurto(aaaaMM: string): string {
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const [ano, mes] = aaaaMM.split('-').map(Number);
  return `${nomes[(mes ?? 1) - 1]}/${String(ano).slice(2)}`;
}

export function plural(quantidade: number, singular: string, plural_: string): string {
  return `${quantidade.toLocaleString('pt-BR')} ${quantidade === 1 ? singular : plural_}`;
}
